<template>
  <div class="feedback-card relative">
    <h2 class="mb-[12px] mt-0 font-[600] text-center">{{ data.title }}</h2>
    <p class="mb-[32px] mt-0 font-[400] text-center text-[var(--text-1-60)]">
      {{ data.desc }}
    </p>
    <swiper
      :modules="[Pagination]"
      :slides-per-view="3"
      :space-between="30"
      :pagination="data.list.length > 3 ? { clickable: true } : false"
      class="customer-voice-swiper"
      @init="onInit"
      @swiper="onSwiper"
      @slideChange="onSlideChange"
    >
      <swiper-slide v-for="(item, idx) in data.list" :key="item.id">
        <div class="flex flex-col p-[24px] rounded-[8px] custom-voice-item relative">
          <div class="flex items-center gap-[8px]">
            <Resource
              :src="item.witness_logo || getFeUrl(`/assets/avatar/a${(idx + 1) % 8}.png`)"
              class="w-[28px] h-[28px] rounded-full"
            />
            <h3 class="mb-0 mt-0 text-[14px]">
              <span class="text-[var(--text-2)] font-[600]">
                {{ displayName(item.witness_name || '')[0] }}
              </span>
              <span
                v-if="displayName(item.witness_name || '').length > 1"
                class="text-[var(--text-1)] font-[400]"
              >
                | {{ displayName(item.witness_name || '')[1] }}
              </span>
            </h3>
          </div>

          <iconpark-icon
            name="quote"
            class="w-[16px] h-[16px] rounded-full mt-[32px] mb-[12px]"
            size="16"
          ></iconpark-icon>

          <div class="flex flex-col gap-[4px]">
            <!-- 最多五行 -->
            <p class="mb-0 mt-0 font-[400] text-[var(--text-1)] leading-[32px] line-clamp-5">
              {{ item.desc }}
            </p>
          </div>
          <Resource
            v-if="item.logo"
            :src="item.logo"
            class="h-[36px] absolute bottom-[24px] right-[24px]"
          />
        </div>
      </swiper-slide>
    </swiper>
    <template v-if="data.list.length > 3">
      <div v-if="!isBeginning" class="swiper-button-prev swiper-button" @click="handlePrev">
        <iconpark-icon name="arrowright" size="16"></iconpark-icon>
      </div>
      <div v-if="!isEnd" class="swiper-button-next swiper-button" @click="handleNext">
        <iconpark-icon name="arrowright" size="16"></iconpark-icon>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { getFeUrl } from '@/utils/url';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

import type { ScenariosConfigDataCard, CustomerVoiceItem } from '~/api/interface';
import { Resource } from '#components';

interface CardProps {
  data: ScenariosConfigDataCard<CustomerVoiceItem>;
}

const props = defineProps<CardProps>();

const displayName = (name: string) => {
  // 根据 | 或者 | 分割
  const split = name.split(/｜|\|/g);
  return split.length > 1 ? split : [name];
};

const swiperRef = ref<SwiperType>();
const isBeginning = ref(true);
const isEnd = ref(false);

const onInit = (swiper: SwiperType) => {
  swiperRef.value = swiper;
};

const onSwiper = (swiper: SwiperType) => {
  // 初始化时更新状态
  isBeginning.value = swiper.isBeginning;
  isEnd.value = swiper.isEnd;
};

const onSlideChange = () => {
  if (!swiperRef.value) return;
  // 滑动时更新状态
  isBeginning.value = swiperRef.value.isBeginning;
  isEnd.value = swiperRef.value.isEnd;
};

const handlePrev = () => {
  swiperRef.value?.slidePrev();
};

const handleNext = () => {
  swiperRef.value?.slideNext();
};
</script>

<style lang="scss" scoped>
h2 {
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  text-align: center;
}

.enterprise-card-carousel {
  width: 1200px;
  margin: 0 auto;
}

.feedback-card {
  width: 1200px;
}
.customer-voice-swiper {
  width: 100%;
  height: auto;
  padding: 12px 0 28px;

  .custom-voice-item {
    border: 1px solid var(--back-5);
    border-radius: 20px;
    height: 384px;
  }

  :deep(.swiper-pagination) {
    bottom: 0;
    line-height: 0;
  }

  :deep(.swiper-pagination-bullet) {
    background: var(--text-3);
    opacity: 0.3;
  }

  :deep(.swiper-pagination-bullet-active) {
    opacity: 1;
    background: var(--primary-color);
  }
}
.swiper-button {
  position: absolute;
  top: calc(50% + 16px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 32px;
  background-color: var(--white-1);
  border: 1px solid var(--back-5);
  z-index: 10;
  color: var(--text-1);
  cursor: pointer;
  &:hover {
    color: var(--primary-color);
  }
  &.disabled {
    cursor: not-allowed;
    color: var(--text-1);
  }
}
.swiper-button-next {
  right: -16px;
  height: 32px;
  width: 32px;
}
.swiper-button-prev {
  height: 32px;
  width: 32px;
  transform-origin: center;
  transform: rotate(180deg);
  left: -16px;
}
</style>
